import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { format, startOfWeek, addDays, isToday, isSameDay } from 'date-fns';
import { el } from 'date-fns/locale';
import { useAuth } from '../auth/AuthContext';
import { useBookings } from '../hooks/useBookings';
import { staffApi } from '../services/api';
import { useAppLayout } from '../components/AppLayout';
import BookingModal from '../components/BookingModal';
import ConflictModal from '../components/ConflictModal';
import './CalendarView.css';

const SLOT_MINUTES     = 5;
const PREVIEW_MINUTES  = 15;
const DAY_LABELS_SHORT = ['ΔΕ','ΤΡ','ΤΕ','ΠΕ','ΠΑ','ΣΑ','ΚΥ'];
const DAY_LABELS_FULL  = ['ΔΕΥ','ΤΡΙ','ΤΕΤ','ΠΕΜ','ΠΑΡ','ΣΑΒ','ΚΥΡ'];
const CAL_STYLE_KEY    = 'salonbook_cal_style';

const timeToMinutes = (t) => { if (!t) return 540; const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); };
const formatTime    = (h, m) => { const s = h < 12 ? 'ΠΜ' : 'ΜΜ'; return `${h % 12 || 12}:${String(m).padStart(2,'0')} ${s}`; };
const endTimeLabel  = (st, dur) => { const e = new Date(new Date(st).getTime() + dur * 60000); return formatTime(e.getHours(), e.getMinutes()); };
const getStoredStyle = () => { try { return localStorage.getItem(CAL_STYLE_KEY) || 'grid'; } catch { return 'grid'; } };
const saveStyle      = (s) => { try { localStorage.setItem(CAL_STYLE_KEY, s); } catch {} };

const useIsMobile = () => {
  const [mobile, setMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mobile;
};

/* ── Outside component: no recreate on render ── */
function StyleToggle({ calStyle, onSwitch }) {
  return (
    <div className="cal-style-toggle" title="Εναλλαγή προβολής">
      <button className={'cal-style-btn' + (calStyle === 'list' ? ' active' : '')}
        onClick={() => onSwitch('list')} title="Λίστα">☰</button>
      <button className={'cal-style-btn' + (calStyle === 'grid' ? ' active' : '')}
        onClick={() => onSwitch('grid')} title="Πλέγμα">⊞</button>
    </div>
  );
}

function Modals({ showBookingModal, selectedSlot, selectedBooking, schedule,
                  onSave, onDelete, onClose,
                  showConflictModal, conflictData, onSelectSlot, onConflictClose }) {
  return (
    <>
      {showBookingModal && (
        <BookingModal initialDate={selectedSlot} existingBooking={selectedBooking}
          schedule={schedule} onSave={onSave} onDelete={onDelete} onClose={onClose} />
      )}
      {showConflictModal && conflictData && (
        <ConflictModal conflict={conflictData} onSelectSlot={onSelectSlot} onClose={onConflictClose} />
      )}
    </>
  );
}

export default function CalendarView() {
  const { openSidebar } = useAppLayout();
  const isMobile = useIsMobile();
  const {
    bookings, loading, currentWeek, schedule,
    fetchBookings, createBooking, updateBooking, cancelBooking, goToWeek
  } = useBookings();

  const [calStyle, setCalStyle]                   = useState(getStoredStyle);
  const [showBookingModal, setShowBookingModal]   = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData]           = useState(null);
  const [selectedSlot, setSelectedSlot]           = useState(null);
  const [selectedBooking, setSelectedBooking]     = useState(null);
  const [pendingBooking, setPendingBooking]       = useState(null);
  const [darkMode, setDarkMode]                   = useState(false);
  const [hoveredCell, setHoveredCell]             = useState(null);
  const [staffList, setStaffList]                 = useState([]);
  const [staffFilter, setStaffFilter]             = useState('all');
  const [viewMode, setViewMode]                   = useState('week');
  const [selectedDay, setSelectedDay]             = useState(new Date());
  const touchStartX = useRef(null);

  // ── Drag & Drop state ──────────────────────────────────────
  const [draggingBooking, setDraggingBooking]     = useState(null);
  const [dragOverCell, setDragOverCell]           = useState(null);
  const draggingRef = useRef(null); // ref για χρήση μέσα σε event handlers

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchBookings(); }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const r = await staffApi.getAll();
        if (!cancelled) setStaffList(r.data.staff || []);
      } catch {
        if (!cancelled && attempt < 3)
          setTimeout(() => load(attempt + 1), 500 * 2 ** attempt);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const switchStyle = (s) => { setCalStyle(s); saveStyle(s); };
  const toggleTheme = () => {
    const n = !darkMode; setDarkMode(n);
    document.documentElement.setAttribute('data-theme', n ? 'dark' : 'light');
  };

  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekDays  = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const getDaySched = useCallback((day) => schedule?.[day.getDay()] || { enabled: true, start: '09:00', end: '20:00' }, [schedule]);

  const SLOTS = useMemo(() => {
    let mn = 540, mx = 1200;
    if (schedule) {
      let fMin = 1380, fMax = 0;
      weekDays.forEach(d => {
        const s = getDaySched(d);
        if (s.enabled) { fMin = Math.min(fMin, timeToMinutes(s.start)); fMax = Math.max(fMax, timeToMinutes(s.end)); }
      });
      if (fMin < fMax) { mn = fMin; mx = fMax; }
    }
    const slots = [];
    for (let m = mn; m < mx; m += SLOT_MINUTES)
      slots.push({ hour: Math.floor(m / 60), minute: m % 60, total: m });
    return slots;
  }, [schedule]); // eslint-disable-line react-hooks/exhaustive-deps

  const bookingsByDay = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      if (b.status === 'cancelled') return;
      if (staffFilter !== 'all' && b.staff_id !== staffFilter) return;
      const key = new Date(b.starts_at).toDateString();
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    return map;
  }, [bookings, staffFilter]);

  const dayBookings = useCallback((day) =>
    (bookingsByDay[day.toDateString()] || [])
      .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at)),
  [bookingsByDay]);

  const slotBookings = useCallback((day, h, m) =>
    (bookingsByDay[day.toDateString()] || [])
      .filter(b => {
        const st = new Date(b.starts_at);
        return st.getHours() === h && st.getMinutes() === m;
      }),
  [bookingsByDay]);

  const inSched = useCallback((day, h, m) => {
    const s = getDaySched(day);
    if (!s.enabled) return false;
    const t = h * 60 + m;
    return t >= timeToMinutes(s.start) && t < timeToMinutes(s.end);
  }, [getDaySched]);

  const handleCellClick = (day, h, m) => {
    if (!inSched(day, h, m)) return;
    const d = new Date(day); d.setHours(h, m, 0, 0);
    setSelectedBooking(null); setSelectedSlot(d); setShowBookingModal(true);
  };
  const handleBookingClick = (e, b) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setSelectedSlot(null); setSelectedBooking(b); setShowBookingModal(true);
  };
  const closeModal = () => { setShowBookingModal(false); setSelectedSlot(null); setSelectedBooking(null); };

  const handleSave = async (data) => {
    if (data.id) { await updateBooking(data.id, data); closeModal(); }
    else {
      const r = await createBooking(data);
      if (r.success) { closeModal(); }
      else { closeModal(); setPendingBooking(data); setConflictData(r.conflict); setShowConflictModal(true); }
    }
  };
  const handleDelete        = async (id) => { await cancelBooking(id); closeModal(); };
  const handleConflictSlot  = async (s) => {
    const r = await createBooking({ ...pendingBooking, starts_at: s });
    if (r.success) { setShowConflictModal(false); setPendingBooking(null); }
  };
  const handleConflictClose = () => { setShowConflictModal(false); setPendingBooking(null); };

  // ── Drag & Drop handlers ──────────────────────────────────
  const handleDragStart = (e, b) => {
    e.stopPropagation();
    setDraggingBooking(b);
    draggingRef.current = b;
    // Φαντομ εικόνα — κάνει το drag πιο καθαρό
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', b.id);
    }
  };

  const handleDragEnd = () => {
    setDraggingBooking(null);
    setDragOverCell(null);
    draggingRef.current = null;
  };

  const handleDragOver = (e, cellKey, active) => {
    if (!active || !draggingRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCell(cellKey);
  };

  const handleDragLeave = (cellKey) => {
    setDragOverCell(prev => prev === cellKey ? null : prev);
  };

  const handleDrop = async (e, day, hour, minute, active) => {
    e.preventDefault();
    setDragOverCell(null);
    const booking = draggingRef.current;
    if (!booking || !active) return;

    // Αποφυγή drop στο ίδιο slot
    const orig = new Date(booking.starts_at);
    if (
      orig.toDateString() === day.toDateString() &&
      orig.getHours() === hour &&
      orig.getMinutes() === minute
    ) {
      setDraggingBooking(null);
      draggingRef.current = null;
      return;
    }

    const newStart = new Date(day);
    newStart.setHours(hour, minute, 0, 0);

    setDraggingBooking(null);
    draggingRef.current = null;

    await updateBooking(booking.id, { starts_at: newStart.toISOString() });
  };

  const chipColor   = (b) => b.staff?.color || b.service?.color || '#534AB7';
  const todayCount  = bookings.filter(b => isToday(new Date(b.starts_at)) && b.status !== 'cancelled').length;
  const weekRevenue = bookings.filter(b => b.status !== 'cancelled' && b.service?.price != null).reduce((s, b) => s + parseFloat(b.service.price), 0);
  const visibleDays = viewMode === 'day' ? [selectedDay] : weekDays;

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) setSelectedDay(d => addDays(d, dx < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  const mobileWeekStart = startOfWeek(selectedDay, { weekStartsOn: 1 });
  const mobileWeekDays  = Array.from({ length: 7 }, (_, i) => addDays(mobileWeekStart, i));

  const sharedModals = (
    <Modals
      showBookingModal={showBookingModal} selectedSlot={selectedSlot}
      selectedBooking={selectedBooking} schedule={schedule}
      onSave={handleSave} onDelete={handleDelete} onClose={closeModal}
      showConflictModal={showConflictModal} conflictData={conflictData}
      onSelectSlot={handleConflictSlot} onConflictClose={handleConflictClose}
    />
  );

  /* ════════════════════════════════════════════════════════════
     LIST STYLE
     ════════════════════════════════════════════════════════════ */
  if (calStyle === 'list') {
    const bks   = dayBookings(selectedDay);
    const sched = getDaySched(selectedDay);

    return (
      <>
        <header className="topbar">
          <div className="topbar-left">
            <button className="hamburger-btn" onClick={openSidebar}>☰</button>
            <h1 className="page-title">Ημερολόγιο</h1>
          </div>
          <div className="topbar-right">
            <StyleToggle calStyle={calStyle} onSwitch={switchStyle} />
            <button className="theme-toggle" onClick={toggleTheme}>{darkMode ? '☀️' : '🌙'}</button>
            <button className="new-booking-btn" onClick={() => { setSelectedBooking(null); setSelectedSlot(selectedDay); setShowBookingModal(true); }}>
              <span className="btn-icon">+</span><span className="btn-label"> Κράτηση</span>
            </button>
          </div>
        </header>

        {/* Mini week strip */}
        <div className="mob-week-strip">
          <button className="mob-week-nav" onClick={() => setSelectedDay(d => addDays(d, -7))}>‹</button>
          <div className="mob-week-days">
            {mobileWeekDays.map((d, i) => {
              const hasBk = (bookingsByDay[d.toDateString()] || []).length > 0;
              return (
                <button key={i} className={'mob-day-btn' + (isSameDay(d, selectedDay) ? ' active' : '') + (isToday(d) ? ' today' : '')}
                  onClick={() => setSelectedDay(d)}>
                  <span className="mob-day-label">{DAY_LABELS_SHORT[i]}</span>
                  <span className="mob-day-num">{format(d, 'd')}</span>
                  <span className={'mob-day-dot' + (hasBk ? ' has-bk' : '')} />
                </button>
              );
            })}
          </div>
          <button className="mob-week-nav" onClick={() => setSelectedDay(d => addDays(d, 7))}>›</button>
        </div>

        {/* Day title */}
        <div className="mob-day-title">
          <button className="mob-nav-arrow" onClick={() => setSelectedDay(d => addDays(d, -1))}>‹</button>
          <div className="mob-day-full">
            {format(selectedDay, 'EEEE d MMMM', { locale: el })}
            {sched.enabled && <span className="mob-day-hours"> · {sched.start}–{sched.end}</span>}
          </div>
          <button className="mob-nav-arrow" onClick={() => setSelectedDay(d => addDays(d, 1))}>›</button>
        </div>

        {staffList.length > 0 && (
          <div className="staff-legend">
            <button className={'staff-pill' + (staffFilter === 'all' ? ' active' : '')} onClick={() => setStaffFilter('all')}>Όλοι</button>
            {staffList.map(s => (
              <button key={s.id} className={'staff-pill' + (staffFilter === s.id ? ' active' : '')}
                style={{ '--pill-color': s.color }} onClick={() => setStaffFilter(staffFilter === s.id ? 'all' : s.id)}>
                <span className="staff-pill-dot" style={{ background: s.color }} />{s.name}
              </button>
            ))}
          </div>
        )}

        <div className="mob-day-content" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          {loading && <div className="mob-loading">Φόρτωση...</div>}

          {!loading && !sched.enabled && (
            <div className="mob-closed">
              <div className="mob-closed-icon">🔒</div>
              <div className="mob-closed-text">Κλειστό σήμερα</div>
            </div>
          )}

          {!loading && sched.enabled && bks.length === 0 && (
            <div className="mob-empty">
              <div className="mob-empty-icon">📭</div>
              <div className="mob-empty-text">Δεν υπάρχουν κρατήσεις</div>
              <button className="mob-empty-btn" onClick={() => { setSelectedBooking(null); setSelectedSlot(selectedDay); setShowBookingModal(true); }}>
                + Νέα κράτηση
              </button>
            </div>
          )}

          {!loading && bks.map(b => {
            const color = chipColor(b);
            const start = new Date(b.starts_at);
            const endDt = new Date(start.getTime() + b.service_duration * 60000);
            return (
              <div key={b.id} className="mob-booking-card"
                style={{ '--card-color': color, borderLeftColor: color }}
                onClick={() => handleBookingClick(null, b)}>
                <div className="mob-card-time">
                  <span className="mob-time-start">{format(start, 'HH:mm')}</span>
                  <span className="mob-time-end">{format(endDt, 'HH:mm')}</span>
                </div>
                <div className="mob-card-body">
                  <div className="mob-card-client">{b.client_name}</div>
                  <div className="mob-card-meta">
                    {b.service_name && <span className="mob-card-service">✂ {b.service_name}</span>}
                    {b.staff && <span className="mob-card-staff" style={{ color }}>· {b.staff.name}</span>}
                    {b.service?.price && <span className="mob-card-price">· {parseFloat(b.service.price).toFixed(2)}€</span>}
                  </div>
                  {b.client_phone && <div className="mob-card-phone">📞 {b.client_phone}</div>}
                </div>
                <div className="mob-card-dur">{b.service_duration}λ</div>
              </div>
            );
          })}

          {!loading && sched.enabled && (
            <button className="mob-add-slot" onClick={() => { setSelectedBooking(null); setSelectedSlot(selectedDay); setShowBookingModal(true); }}>
              <span>+</span> Νέα κράτηση
            </button>
          )}
        </div>

        <div className="stats-bar">
          <div className="stat"><div className="stat-label">Σήμερα</div><div className="stat-value">{todayCount}</div></div>
          <div className="stat"><div className="stat-label">Εβδομάδα</div><div className="stat-value">{bookings.filter(b => b.status !== 'cancelled').length}</div></div>
          <div className="stat"><div className="stat-label">Επιβεβ.</div><div className="stat-value">{bookings.filter(b => b.status === 'confirmed').length}</div></div>
          <div className="stat"><div className="stat-label">Έσοδα</div><div className="stat-value" style={{ color: 'var(--green)' }}>{weekRevenue > 0 ? `€${weekRevenue.toFixed(0)}` : '—'}</div></div>
        </div>

        {sharedModals}
      </>
    );
  }

  /* ════════════════════════════════════════════════════════════
     GRID STYLE  (με Drag & Drop)
     ════════════════════════════════════════════════════════════ */
  return (
    <>
      <header className="topbar grid-topbar">
        <div className="grid-topbar-row1">
          <div className="topbar-left">
            <button className="hamburger-btn" onClick={openSidebar}>☰</button>
            <h1 className="page-title">Ημερολόγιο</h1>
          </div>
          <div className="topbar-right">
            <StyleToggle calStyle={calStyle} onSwitch={switchStyle} />
            <button className="theme-toggle" onClick={toggleTheme}>{darkMode ? '☀️' : '🌙'}</button>
            <button className="new-booking-btn" onClick={() => { setSelectedBooking(null); setSelectedSlot(new Date()); setShowBookingModal(true); }}>
              <span className="btn-icon">+</span><span className="btn-label"> Κράτηση</span>
            </button>
          </div>
        </div>
        <div className="grid-topbar-row2">
          <div className="week-nav">
            {viewMode === 'week' ? (
              <>
                <button className="nav-btn" onClick={() => goToWeek(-1)}>‹</button>
                <span className="week-label">{format(weekStart, 'MMM yyyy', { locale: el })}</span>
                <button className="nav-btn" onClick={() => goToWeek(1)}>›</button>
              </>
            ) : (
              <>
                <button className="nav-btn" onClick={() => setSelectedDay(d => addDays(d, -1))}>‹</button>
                <span className="week-label">{format(selectedDay, 'EEE d MMM', { locale: el })}</span>
                <button className="nav-btn" onClick={() => setSelectedDay(d => addDays(d, 1))}>›</button>
              </>
            )}
          </div>
          <div className="view-toggle">
            <button className={'view-btn' + (viewMode === 'week' ? ' active' : '')} onClick={() => setViewMode('week')}>Εβδ</button>
            <button className={'view-btn' + (viewMode === 'day'  ? ' active' : '')} onClick={() => { setViewMode('day'); setSelectedDay(new Date()); }}>Ημ</button>
          </div>
        </div>
      </header>

      {staffList.length > 0 && (
        <div className="staff-legend">
          <button className={'staff-pill' + (staffFilter === 'all' ? ' active' : '')} onClick={() => setStaffFilter('all')}>Όλοι</button>
          {staffList.map(s => (
            <button key={s.id} className={'staff-pill' + (staffFilter === s.id ? ' active' : '')}
              style={{ '--pill-color': s.color }} onClick={() => setStaffFilter(staffFilter === s.id ? 'all' : s.id)}>
              <span className="staff-pill-dot" style={{ background: s.color }} />{s.name}
            </button>
          ))}
        </div>
      )}

      <div className="calendar-container">
        {loading && <div className="loading-overlay">Φόρτωση...</div>}

        <div className={'cal-header' + (viewMode === 'day' ? ' cal-header-day' : '')}>
          <div className="time-gutter" />
          {visibleDays.map((day, i) => {
            const s = getDaySched(day);
            return (
              <div key={i} className={'day-header' + (isToday(day) ? ' today' : '') + (!s.enabled ? ' closed' : '')}
                onClick={() => { if (viewMode === 'week') { setSelectedDay(day); setViewMode('day'); } }}
                style={viewMode === 'week' ? { cursor: 'pointer' } : {}}>
                <div className="day-label">{viewMode === 'day' ? format(day, 'EEEE', { locale: el }).toUpperCase() : DAY_LABELS_FULL[i]}</div>
                <div className={'day-num' + (isToday(day) ? ' today-circle' : '')}>{format(day, 'd')}</div>
                {!s.enabled ? <div className="day-closed-label">Κλειστό</div> : <div className="day-hours-label">{s.start} – {s.end}</div>}
              </div>
            );
          })}
        </div>

        <div className="cal-grid">
          {SLOTS.map(({ hour, minute, total }) => {
            const isMajor = total % 30 === 0;
            return (
              <div key={`${hour}-${minute}`}
                className={'cal-row ' + (isMajor ? 'cal-row-major' : 'cal-row-minor') + (viewMode === 'day' ? ' cal-row-day' : '')}>
                <div className="time-label">{isMajor ? formatTime(hour, minute) : ''}</div>
                {visibleDays.map((day, di) => {
                  const active   = inSched(day, hour, minute);
                  const bks      = slotBookings(day, hour, minute);
                  const cellKey  = `${day.toDateString()}-${hour}-${minute}`;
                  const hovered  = hoveredCell === cellKey;
                  const isDragOver = dragOverCell === cellKey && active;

                  return (
                    <div key={di}
                      className={
                        'cal-cell' +
                        (!active ? ' cell-closed' : '') +
                        (hovered && active && !draggingBooking ? ' cell-hovered' : '') +
                        (isDragOver ? ' cell-drag-over' : '')
                      }
                      onClick={() => !draggingBooking && handleCellClick(day, hour, minute)}
                      onMouseEnter={() => active && !draggingBooking && setHoveredCell(cellKey)}
                      onMouseLeave={() => setHoveredCell(null)}
                      onDragOver={(e) => handleDragOver(e, cellKey, active)}
                      onDragLeave={() => handleDragLeave(cellKey)}
                      onDrop={(e) => handleDrop(e, day, hour, minute, active)}
                    >
                      {hovered && active && bks.length === 0 && !draggingBooking && (
                        <div className="cell-time-preview">
                          {formatTime(hour, minute)} – {(() => { const e = total + PREVIEW_MINUTES; return formatTime(Math.floor(e / 60), e % 60); })()}
                        </div>
                      )}
                      {bks.map(b => (
                        <div key={b.id}
                          className={'booking-chip' + (draggingBooking?.id === b.id ? ' chip-dragging' : '')}
                          draggable
                          onDragStart={(e) => handleDragStart(e, b)}
                          onDragEnd={handleDragEnd}
                          style={{ background: chipColor(b) + '18', borderLeftColor: chipColor(b), cursor: 'grab' }}
                          onClick={(e) => { if (!draggingBooking) handleBookingClick(e, b); }}>
                          <div className="chip-time" style={{ color: chipColor(b) }}>
                            {formatTime(new Date(b.starts_at).getHours(), new Date(b.starts_at).getMinutes())} – {endTimeLabel(b.starts_at, b.service_duration)}
                          </div>
                          <div className="chip-name">{b.client_name}</div>
                          <div className="chip-meta">
                            <span className="chip-service">{b.service_name || '—'}</span>
                            {b.staff && <span className="chip-staff" style={{ color: chipColor(b) }}>• {b.staff.name}</span>}
                            {b.service?.price && <span className="chip-price" style={{ color: chipColor(b) }}>• {parseFloat(b.service.price).toFixed(2)}€</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="stats-bar">
          <div className="stat"><div className="stat-label">Σήμερα</div><div className="stat-value">{todayCount}</div></div>
          <div className="stat"><div className="stat-label">Εβδομάδα</div><div className="stat-value">{bookings.filter(b => b.status !== 'cancelled').length}</div></div>
          <div className="stat"><div className="stat-label">Επιβεβαιωμένες</div><div className="stat-value">{bookings.filter(b => b.status === 'confirmed').length}</div></div>
          <div className="stat"><div className="stat-label">Έσοδα εβδ.</div><div className="stat-value" style={{ color: 'var(--green)' }}>{weekRevenue > 0 ? `€${weekRevenue.toFixed(0)}` : '—'}</div></div>
        </div>
      </div>

      {sharedModals}
    </>
  );
}